// Enhanced AdminCustomer.jsx with green theme matching AdminPackages
import React, { useState, useEffect } from 'react';
import SidebarHeader from '../../components/admin/sidebar';
import { useAuth } from '../../pages/auth/AuthContext';
import RequireAdminRole from '../../components/auth/userole';
import { 
  Search, 
  Filter, 
  RefreshCw, 
  Eye, 
  Edit, 
  Trash2,
  User,
  Users,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Plus,
  Mail,
  Phone,
  Calendar,
  Shield
} from 'lucide-react';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const ROLE_OPTIONS = [
  { value: '', label: 'Semua Role' },
  { value: 'customer', label: 'Customer' },
  { value: 'admin', label: 'Admin' },
  { value: 'technician', label: 'Technician' },
  { value: 'sales', label: 'Sales' }
];

const STATUS_OPTIONS = [
  { value: '', label: 'Semua Status' },
  { value: 'true', label: 'Aktif' },
  { value: 'false', label: 'Nonaktif' }
];

const AdminCustomer = () => {
  const { user, loading } = useAuth();
  const { user: contextUser, token: contextToken } = useAuth();
  
  const [activeTab, setActiveTab] = useState('customers');
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    name: contextUser?.fullName || contextUser?.name || 'Admin User',
    email: contextUser?.email || 'admin@telnet.co.id',
    role: contextUser?.role || 'Super Admin'
  });

  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Form states
  const [userForm, setUserForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: 'customer',
    isActive: true,
    password: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const LIMIT = 10;

  // Enhanced token handling
  const authToken = contextToken ||
                    localStorage.getItem('adminToken') ||
                    localStorage.getItem('token') ||
                    sessionStorage.getItem('adminToken') ||
                    sessionStorage.getItem('token');

  // Enhanced auth headers function
  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    return headers;
  };

  // Format tanggal
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Enhanced fetch users with better error handling
  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: LIMIT.toString(),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter !== '' && { isActive: statusFilter }),
        ...(search && { search })
      });

      let response;
      const headers = getAuthHeaders();

      const adminUsersUrl = `${API_BASE_URL}/api/users?${params.toString()}`;

      response = await fetch(adminUsersUrl, {
        method: 'GET',
        headers,
        credentials: authToken ? 'include' : 'omit'
      });

      if (!response.ok && response.status === 401) {
        response = await fetch(adminUsersUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to view users.');
        } else if (response.status === 404) {
          throw new Error('Users endpoint not found. Please check your API configuration.');
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}: Failed to fetch users`);
        }
      }

      const data = await response.json();

      if (data.success) {
        const usersData = Array.isArray(data.data) ? data.data : [];
        setUsers(usersData);
        setTotalPages(data.pagination?.totalPages || Math.ceil(usersData.length / LIMIT) || 1);
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to load users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      
      let errorMessage = error.message;
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setUsers([]);
      setTotalPages(1);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Form validation
  const validateForm = (formData, isEdit = false) => {
    const errors = {};

    if (!formData.fullName || formData.fullName.trim().length < 2) {
      errors.fullName = 'Nama lengkap minimal 2 karakter';
    }

    if (!formData.email || !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(formData.email)) {
      errors.email = 'Format email tidak valid';
    }

    if (formData.phone && !/^(\+62|62|0)8[1-9][0-9]{6,9}$/.test(formData.phone)) {
      errors.phone = 'Format nomor telepon tidak valid';
    }

    if (!isEdit) {
      if (!formData.password || formData.password.length < 6) {
        errors.password = 'Password minimal 6 karakter';
      }

      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Konfirmasi password tidak sama';
      }
    }

    return errors;
  };

  // Create user
  const handleCreateUser = async () => {
    try {
      setFormErrors({});
      
      const errors = validateForm(userForm);
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit',
        body: JSON.stringify({
          fullName: userForm.fullName,
          email: userForm.email,
          phone: userForm.phone,
          role: userForm.role,
          isActive: userForm.isActive,
          password: userForm.password,
          confirmPassword: userForm.confirmPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to create user`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert('User berhasil dibuat!');
        setShowCreateModal(false);
        resetUserForm();
        fetchUsers();
      } else {
        throw new Error(data.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Error creating user: ' + error.message);
    }
  };

  // Update user
  const handleUpdateUser = async () => {
    try {
      if (!selectedUser) return;
      
      setFormErrors({});
      
      const errors = validateForm(userForm, true);
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/users/${selectedUser._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit',
        body: JSON.stringify({
          fullName: userForm.fullName,
          email: userForm.email,
          phone: userForm.phone,
          role: userForm.role,
          isActive: userForm.isActive
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to update user`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert('User berhasil diupdate!');
        setShowEditModal(false);
        setSelectedUser(null);
        resetUserForm();
        fetchUsers();
      } else {
        throw new Error(data.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user: ' + error.message);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    try {
      if (!selectedUser) return;

      const response = await fetch(`${API_BASE_URL}/api/users/${selectedUser._id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to delete user`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert('User berhasil dihapus!');
        setShowDeleteModal(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        throw new Error(data.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user: ' + error.message);
    }
  };

  // Reset user form
  const resetUserForm = () => {
    setUserForm({
      fullName: '',
      email: '',
      phone: '',
      role: 'customer',
      isActive: true,
      password: '',
      confirmPassword: ''
    });
    setFormErrors({});
  };

  // Open modals
  const openCreateModal = () => {
    resetUserForm();
    setShowCreateModal(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setUserForm({
      fullName: user.fullName || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'customer',
      isActive: user.isActive !== false,
      password: '',
      confirmPassword: ''
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const openDetailModal = (user) => {
    setSelectedUser(user);
    setShowDetailModal(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Update current user when context changes
  useEffect(() => {
    if (contextUser) {
      setCurrentUser({
        name: contextUser.fullName || contextUser.name || 'Admin User',
        email: contextUser.email || 'admin@telnet.co.id',
        role: contextUser.role || 'Super Admin'
      });
    }
  }, [contextUser]);

  useEffect(() => {
    fetchUsers();
  }, [page, roleFilter, statusFilter, authToken]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleRetry = () => {
    fetchUsers();
  };

  // Stats Cards Component with green theme
  const StatsCards = () => {
    const stats = {
      total: users.length,
      active: users.filter(user => user.isActive !== false).length,
      customers: users.filter(user => user.role === 'customer').length,
      staff: users.filter(user => ['admin', 'technician', 'sales'].includes(user.role)).length
    };

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Users className="text-blue-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Customers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.customers}</p>
            </div>
            <User className="text-yellow-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Staff Members</p>
              <p className="text-2xl font-bold text-gray-900">{stats.staff}</p>
              <button 
                onClick={fetchUsers}
                disabled={isLoading}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center mt-1"
              >
                <RefreshCw size={12} className={`mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                {isLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            <Shield className="text-purple-500" size={32} />
          </div>
        </div>
      </div>
    );
  };

  // Mobile Header Component with green theme
  const MobileHeader = () => (
    <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Manage Users</h1>
            <p className="text-xs text-gray-600">{currentUser.role}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {error && (
            <button
              onClick={handleRetry}
              className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 transition text-xs"
            >
              Retry
            </button>
          )}
          <div className="h-8 w-8 bg-green-600 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {currentUser.name.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Sidebar Overlay for Mobile
  const SidebarOverlay = () => (
    <>
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );

  // Mobile User Card Component with green theme
  const MobileUserCard = ({ user }) => (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <button
            onClick={() => openDetailModal(user)}
            className="text-sm font-medium text-green-600 hover:text-green-800 truncate block text-left"
          >
            {user.fullName || 'N/A'}
          </button>
          <div className="flex items-center space-x-1 text-sm text-gray-600 mb-1">
            <Mail size={12} />
            <span className="truncate">{user.email}</span>
          </div>
          {user.phone && (
            <div className="flex items-center space-x-1 text-sm text-gray-600 mb-2">
              <Phone size={12} />
              <span>{user.phone}</span>
            </div>
          )}
          
          <div className="flex flex-wrap gap-2 mb-2">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${
              user.role === 'admin' ? 'bg-purple-100 text-purple-800 border-purple-200' :
              user.role === 'technician' ? 'bg-blue-100 text-blue-800 border-blue-200' :
              user.role === 'sales' ? 'bg-orange-100 text-orange-800 border-orange-200' :
              'bg-gray-100 text-gray-800 border-gray-200'
            }`}>
              {user.role || 'N/A'}
            </span>
            
            {user.isActive !== false ? (
              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                Aktif
              </span>
            ) : (
              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
                Nonaktif
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-1 text-xs text-gray-500">
            <Calendar size={10} />
            <span>Terdaftar: {formatDate(user.createdAt)}</span>
          </div>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => openEditModal(user)}
          className="flex-1 min-w-0 text-green-600 hover:text-green-900 text-xs bg-green-100 px-3 py-2 rounded-md hover:bg-green-200 transition flex items-center justify-center space-x-1"
        >
          <Edit size={12} />
          <span>Edit</span>
        </button>
        <button
          onClick={() => openDetailModal(user)}
          className="flex-1 min-w-0 text-blue-600 hover:text-blue-900 text-xs bg-blue-100 px-3 py-2 rounded-md hover:bg-blue-200 transition flex items-center justify-center space-x-1"
        >
          <Eye size={12} />
          <span>Detail</span>
        </button>
        <button
          onClick={() => openDeleteModal(user)}
          className="flex-1 min-w-0 text-red-600 hover:text-red-900 text-xs bg-red-100 px-3 py-2 rounded-md hover:bg-red-200 transition flex items-center justify-center space-x-1"
        >
          <Trash2 size={12} />
          <span>Hapus</span>
        </button>
      </div>
    </div>
  );

  // Desktop Table Component with green theme
  const DesktopTable = () => (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telepon</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Terdaftar</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Aksi</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user) => (
            <tr key={user._id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 cursor-pointer hover:text-green-800"
                  onClick={() => openDetailModal(user)}>
                <div className="flex items-center space-x-2">
                  <User size={16} className="text-gray-400" />
                  <span>{user.fullName || '-'}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-2">
                  <Mail size={14} className="text-gray-400" />
                  <span>{user.email}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {user.phone ? (
                  <div className="flex items-center space-x-2">
                    <Phone size={14} className="text-gray-400" />
                    <span>{user.phone}</span>
                  </div>
                ) : (
                  '-'
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${
                  user.role === 'admin' ? 'bg-purple-100 text-purple-800 border-purple-200' :
                  user.role === 'technician' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                  user.role === 'sales' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                  'bg-gray-100 text-gray-800 border-gray-200'
                }`}>
                  {user.role || '-'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-2">
                  {user.isActive !== false ? (
                    <CheckCircle size={14} className="text-green-600" />
                  ) : (
                    <XCircle size={14} className="text-red-600" />
                  )}
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${
                    user.isActive !== false 
                      ? 'bg-green-100 text-green-800 border-green-200' 
                      : 'bg-red-100 text-red-800 border-red-200'
                  }`}>
                    {user.isActive !== false ? 'Aktif' : 'Nonaktif'}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-2">
                  <Calendar size={14} className="text-gray-400" />
                  <span>{formatDate(user.createdAt)}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                <div className="flex space-x-1 justify-center">
                  <button
                    onClick={() => openEditModal(user)}
                    className="text-green-600 hover:text-green-900 text-xs bg-green-100 px-3 py-2 rounded-md hover:bg-green-200 transition flex items-center space-x-1"
                    title="Edit User"
                  >
                    <Edit size={12} />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => openDetailModal(user)}
                    className="text-blue-600 hover:text-blue-900 text-xs bg-blue-100 px-3 py-2 rounded-md hover:bg-blue-200 transition flex items-center space-x-1"
                    title="View Details"
                  >
                    <Eye size={12} />
                    <span>Detail</span>
                  </button>
                  <button
                    onClick={() => openDeleteModal(user)}
                    className="text-red-600 hover:text-red-900 text-xs bg-red-100 px-3 py-2 rounded-md hover:bg-red-200 transition flex items-center space-x-1"
                    title="Delete User"
                  >
                    <Trash2 size={12} />
                    <span>Hapus</span>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <RequireAdminRole user={user} loading={loading}>
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <SidebarOverlay />
        
        {/* Sidebar - Mobile responsive */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <SidebarHeader 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
            currentUser={currentUser}
            onClose={() => setSidebarOpen(false)}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <MobileHeader />

          <main className="flex-1 overflow-auto p-3 sm:p-6">
            {/* Main Panel dengan green theme */}
            <div className="bg-white rounded-lg shadow-xl">
              {/* Header dengan gradient hijau */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-t-lg flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <Users className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h1 className="font-bold text-xl">User Management</h1>
                    <div className="flex items-center space-x-2 text-sm">
                      <span className="text-green-100">
                        {users.length} users ditemukan • Page {page} dari {totalPages}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={fetchUsers}
                    disabled={isLoading}
                    className="hover:bg-green-700 p-2 rounded transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
                  </button>
                  <button
                    onClick={openCreateModal}
                    className="bg-white text-green-600 hover:bg-green-50 px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <Plus size={20} />
                    <span>Tambah User</span>
                  </button>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="p-4 border-b border-gray-200">
                <StatsCards />
              </div>

              {/* Filters and Search */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search */}
                  <form onSubmit={handleSearchSubmit} className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        placeholder="Cari berdasarkan nama atau email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </form>

                  {/* Filter Toggle untuk Mobile */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="lg:hidden flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Filter size={20} />
                    <span>Filter</span>
                    {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>

                  {/* Filters */}
                  <div className={`flex flex-col lg:flex-row gap-2 ${showFilters ? 'block' : 'hidden lg:flex'}`}>
                    <select
                      value={roleFilter}
                      onChange={(e) => {
                        setRoleFilter(e.target.value);
                        setPage(1);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="mx-4 mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span>⚠️</span>
                      <span>{error}</span>
                    </div>
                    <button
                      onClick={handleRetry}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                    >
                      Coba Lagi
                    </button>
                  </div>
                </div>
              )}

              {/* Users Content */}
              <div className="p-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center">
                      <RefreshCw size={32} className="animate-spin text-green-600 mb-2" />
                      <span className="text-gray-500">Loading users...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center max-w-md mx-auto">
                      <div className="text-red-600 mb-4">
                        <span className="text-4xl">⚠️</span>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Users</h3>
                      <p className="text-gray-600 mb-4">{error}</p>
                      <button
                        onClick={handleRetry}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                      <Users size={64} className="mx-auto opacity-30" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Users Found</h3>
                    <p className="text-gray-600">
                      {search || roleFilter || statusFilter ? 'Try adjusting your search filters' : 'No users have been created yet'}
                    </p>
                    <button
                      onClick={openCreateModal}
                      className="mt-4 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
                    >
                      Create First User
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Mobile Cards */}
                    <div className="sm:hidden">
                      {users.map((user) => (
                        <MobileUserCard key={user._id} user={user} />
                      ))}
                    </div>

                    {/* Desktop Table */}
                    <div className="hidden sm:block">
                      <DesktopTable />
                    </div>
                  </>
                )}

                {/* Pagination */}
                {!error && !isLoading && users.length > 0 && (
                  <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-3 sm:space-y-0">
                    <button
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                      className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Sebelumnya
                    </button>
                    <span className="text-sm text-gray-700">
                      Halaman {page} dari {totalPages} ({users.length} users)
                    </span>
                    <button
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                      className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Berikutnya
                    </button>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Tambah User Baru</h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nama Lengkap *</label>
                    <input
                      type="text"
                      value={userForm.fullName}
                      onChange={(e) => setUserForm({...userForm, fullName: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Masukkan nama lengkap"
                    />
                    {formErrors.fullName && <p className="text-red-500 text-xs mt-1">{formErrors.fullName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Masukkan email"
                    />
                    {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telepon</label>
                    <input
                      type="text"
                      value={userForm.phone}
                      onChange={(e) => setUserForm({...userForm, phone: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Masukkan nomor telepon"
                    />
                    {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                    <select
                      value={userForm.role}
                      onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {ROLE_OPTIONS.slice(1).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
                    <input
                      type="password"
                      value={userForm.password}
                      onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Masukkan password"
                    />
                    {formErrors.password && <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Konfirmasi Password *</label>
                    <input
                      type="password"
                      value={userForm.confirmPassword}
                      onChange={(e) => setUserForm({...userForm, confirmPassword: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Konfirmasi password"
                    />
                    {formErrors.confirmPassword && <p className="text-red-500 text-xs mt-1">{formErrors.confirmPassword}</p>}
                  </div>
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={userForm.isActive}
                        onChange={(e) => setUserForm({...userForm, isActive: e.target.checked})}
                        className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">User Aktif</span>
                    </label>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateUser}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Edit User</h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nama Lengkap *</label>
                    <input
                      type="text"
                      value={userForm.fullName}
                      onChange={(e) => setUserForm({...userForm, fullName: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    {formErrors.fullName && <p className="text-red-500 text-xs mt-1">{formErrors.fullName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telepon</label>
                    <input
                      type="text"
                      value={userForm.phone}
                      onChange={(e) => setUserForm({...userForm, phone: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                    <select
                      value={userForm.role}
                      onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {ROLE_OPTIONS.slice(1).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={userForm.isActive}
                        onChange={(e) => setUserForm({...userForm, isActive: e.target.checked})}
                        className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">User Aktif</span>
                    </label>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateUser}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete User Modal */}
        {showDeleteModal && selectedUser && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-auto">
              <div className="p-4 sm:p-5">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Delete User</h3>
                <p className="text-sm text-gray-700 mb-6">
                  Apakah Anda yakin ingin menghapus user <strong>{selectedUser.fullName}</strong>? 
                  Tindakan ini tidak dapat dibatalkan.
                </p>
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteUser}
                    className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm sm:text-base"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Detail Modal */}
        {showDetailModal && selectedUser && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">User Details</h3>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Basic Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <User size={18} />
                      <span>Informasi Dasar</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">ID:</span> {selectedUser._id}</div>
                      <div><span className="font-medium">Nama Lengkap:</span> {selectedUser.fullName || 'N/A'}</div>
                      <div className="flex items-center space-x-2">
                        <Mail size={14} className="text-gray-400" />
                        <span><span className="font-medium">Email:</span> {selectedUser.email}</span>
                      </div>
                      {selectedUser.phone && (
                        <div className="flex items-center space-x-2">
                          <Phone size={14} className="text-gray-400" />
                          <span><span className="font-medium">Telepon:</span> {selectedUser.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">Role:</span> 
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${
                          selectedUser.role === 'admin' ? 'bg-purple-100 text-purple-800 border-purple-200' :
                          selectedUser.role === 'technician' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                          selectedUser.role === 'sales' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                          'bg-gray-100 text-gray-800 border-gray-200'
                        }`}>
                          {selectedUser.role || 'N/A'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">Status:</span> 
                        {selectedUser.isActive !== false ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                            Aktif
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
                            Nonaktif
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">Email Verified:</span> 
                        {selectedUser.isEmailVerified ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
                            Not Verified
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Account Activity */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <Calendar size={18} />
                      <span>Aktivitas Akun</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Dibuat:</span> {formatDateTime(selectedUser.createdAt)}</div>
                      <div><span className="font-medium">Terakhir Update:</span> {formatDateTime(selectedUser.updatedAt)}</div>
                      <div><span className="font-medium">Login Terakhir:</span> {formatDateTime(selectedUser.lastLogin)}</div>
                      <div><span className="font-medium">Jumlah Login:</span> {selectedUser.loginCount || 0}</div>
                      <div><span className="font-medium">Provider Login:</span> {selectedUser.lastLoginProvider || 'N/A'}</div>
                    </div>
                  </div>

                  {/* Profile Information */}
                  {selectedUser.profile && (
                    <div className="bg-gray-50 p-4 rounded-lg lg:col-span-2">
                      <h4 className="font-semibold text-gray-900 mb-3">Informasi Profil</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <div><span className="font-medium">Tanggal Lahir:</span> {selectedUser.profile.dateOfBirth ? formatDate(selectedUser.profile.dateOfBirth) : 'N/A'}</div>
                          <div><span className="font-medium">Jenis Kelamin:</span> {selectedUser.profile.gender || 'N/A'}</div>
                        </div>
                        {selectedUser.profile.address && (
                          <div>
                            <div><span className="font-medium">Provinsi:</span> {selectedUser.profile.address.province || 'N/A'}</div>
                            <div><span className="font-medium">Kota:</span> {selectedUser.profile.address.city || 'N/A'}</div>
                            <div><span className="font-medium">Kecamatan:</span> {selectedUser.profile.address.district || 'N/A'}</div>
                            <div><span className="font-medium">Kode Pos:</span> {selectedUser.profile.address.postalCode || 'N/A'}</div>
                            <div><span className="font-medium">Alamat Lengkap:</span> {selectedUser.profile.address.fullAddress || 'N/A'}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      openEditModal(selectedUser);
                    }}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
                  >
                    Edit User
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </RequireAdminRole>
  );
};

export default AdminCustomer;